import { apiClient } from './client';

// ── Types (mirror myLedger IIntegrationClientService DTOs) ────────────────────
/** The myPOS company a key is linked to (null when the key is not linked). */
export interface BoundSourceDto {
  sourceSystem?: string | null;
  sourceTenantId?: string | null;
  sourceCompanyId: string;
  sourceCompanyCode?: string | null;
  sourceCompanyName?: string | null;
  boundAt?: string | null;
}

export interface ApiClientDto {
  id: string;
  name: string;
  sourceSystem: string;
  keyPrefix: string;
  permissions: string[];
  isActive: boolean;
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdDate: string;
  bound?: BoundSourceDto | null;
}

/** Returned exactly once on creation — carries the one-time plaintext `apiKey`. */
export interface CreatedApiClientDto {
  id: string;
  name: string;
  sourceSystem: string;
  keyPrefix: string;
  apiKey: string;
  permissions: string[];
  expiresAt?: string | null;
}

export interface CreateApiClientRequest {
  name: string;
  sourceSystem: string;
  /** Omit / empty ⇒ backend grants the full integration-posting permission set. */
  permissions?: string[];
  expiresAt?: string | null;
}

/**
 * The ONLY permission codes a service account may hold. Mirrors the backend
 * whitelist in IntegrationClientService.AllowedPermissions — service accounts
 * can never be granted administrative permissions (privilege-escalation guard).
 * Labels are UI-only; the codes are authoritative.
 */
export const INTEGRATION_CLIENT_PERMISSIONS: { code: string; label: string; hint: string }[] = [
  { code: 'VIEW_INT_POSTING',     label: 'View posting batches',   hint: 'Read batches this system submitted.' },
  { code: 'SUBMIT_INT_POSTING',   label: 'Submit postings',        hint: 'Push new posting batches into myLedger.' },
  { code: 'VALIDATE_INT_POSTING', label: 'Validate postings',      hint: 'Run pre-post validation on a batch.' },
  { code: 'POST_INT_POSTING',     label: 'Post to the ledger',     hint: 'Generate the GL journal from a batch.' },
  { code: 'RETRY_INT_POSTING',    label: 'Retry failed postings',  hint: 'Re-attempt a batch that errored.' },
  { code: 'CANCEL_INT_POSTING',   label: 'Cancel postings',        hint: 'Cancel a batch before it posts.' },
  { code: 'INGEST_INT_MAPPING',   label: 'Push masters to mapping', hint: 'Send suppliers/customers to the mapping queue.' },
  { code: 'VIEW_INT_MASTERS',     label: 'Read myLedger masters',  hint: 'Pull accounts / financial periods for mapping.' },
];

// ── API ───────────────────────────────────────────────────────────────────────
export const integrationClientApi = {
  async list(): Promise<ApiClientDto[]> {
    const res = await apiClient.get('/integration-clients');
    return res.data?.data ?? [];
  },

  async create(request: CreateApiClientRequest): Promise<CreatedApiClientDto> {
    const res = await apiClient.post('/integration-clients', request);
    return res.data?.data as CreatedApiClientDto;
  },

  async revoke(id: string): Promise<void> {
    await apiClient.post(`/integration-clients/${id}/revoke`);
  },

  /** Clear the key's myPOS company link. The key itself cannot do this. */
  async unlink(id: string): Promise<void> {
    await apiClient.post(`/integration-clients/${id}/unlink`);
  },
};
